import type { PermissionState } from '@capacitor/core';
import type { NotificationSettings } from '@h2h/shared';
import {
  Bell,
  ChevronRight,
  Megaphone,
  Swords,
  Target,
  TrendingUp,
  Trophy,
  Users
} from '../../components/ui/icons';
import { NationalFlagEmblem } from '../../components/ui/NationalFlagEmblems';
import { teamGradient } from '../../lib/teams';
import { ActionRow, Divider, Group, Row, SectionLabel, ToggleRow } from './SettingsRows';
import type { JoinedLeagueOption } from './types';

export function GameplaySection({
  favourite,
  defaultLeague,
  joinedLeagues,
  pickReminders,
  onPickTeam,
  onPickLeague,
  onPickRemindersChange
}: {
  favourite?: { name: string; code: string } | null;
  defaultLeague?: JoinedLeagueOption;
  joinedLeagues: JoinedLeagueOption[];
  pickReminders: boolean;
  onPickTeam: () => void;
  onPickLeague: () => void;
  onPickRemindersChange: (v: boolean) => void;
}) {
  return (
    <>
      <SectionLabel>Gameplay</SectionLabel>
      <Group>
        <ActionRow
          iconNode={
            <div
              className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-[9px] text-[9px] font-extrabold text-white"
              style={{ backgroundImage: teamGradient(favourite?.code ?? 'H2H') }}
            >
              {favourite?.code ?? '?'}
            </div>
          }
          label="Favourite team"
          onClick={onPickTeam}
          value={favourite?.name ?? 'Choose'}
        />
        <Divider />
        <ActionRow
          iconNode={
            defaultLeague ? (
              <NationalFlagEmblem
                leagueId={defaultLeague.leagueId}
                country={defaultLeague.country}
                size="mini"
              />
            ) : (
              <div className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-[9px] bg-gradient-to-br from-night2 to-night text-[9px] font-extrabold text-white">
                ?
              </div>
            )
          }
          label="Default league"
          onClick={() => joinedLeagues.length > 0 && onPickLeague()}
          value={defaultLeague?.name ?? (joinedLeagues.length > 0 ? 'Choose' : 'Join a league')}
        />
        <Divider />
        <ToggleRow
          icon={<Bell width={17} height={17} strokeWidth={2} className="text-white" />}
          iconBg="#24C56A"
          label="Lock reminders"
          sub="Nudge me before my cards lock"
          checked={pickReminders}
          onChange={onPickRemindersChange}
        />
      </Group>
    </>
  );
}

export function NotificationsSection({
  pushSupported,
  pushPerm,
  notifs,
  onEnablePush,
  onNotifChange
}: {
  pushSupported: boolean;
  pushPerm: PermissionState | null;
  notifs: NotificationSettings;
  onEnablePush: () => void;
  onNotifChange: (key: keyof NotificationSettings, value: boolean) => void;
}) {
  return (
    <>
      <SectionLabel>Notifications</SectionLabel>
      <Group>
        {pushSupported && pushPerm != null && (
          <>
            {pushPerm === 'granted' ? (
              <Row
                icon={<Bell width={16} height={16} strokeWidth={2} className="text-white" />}
                iconBg="#24C56A"
                label="Push notifications"
              >
                <span className="text-[14px] font-semibold text-accent-ink">On</span>
              </Row>
            ) : pushPerm === 'denied' ? (
              <Row
                icon={<Bell width={16} height={16} strokeWidth={2} className="text-white" />}
                iconBg="#6E6E73"
                label="Push notifications"
              >
                <span className="text-[13px] font-medium text-ink2">Off · enable in iOS Settings</span>
              </Row>
            ) : (
              <ActionRow
                icon={<Bell width={16} height={16} strokeWidth={2} className="text-white" />}
                iconBg="#24C56A"
                label="Turn on push notifications"
                onClick={onEnablePush}
                trailing={<ChevronRight width={18} height={18} strokeWidth={2.4} className="text-ink4" />}
              />
            )}
            <Divider />
          </>
        )}
        <ToggleRow
          icon={<Bell width={16} height={16} strokeWidth={2} className="text-white" />}
          iconBg="#24C56A"
          label="New round opens"
          checked={notifs.newRound}
          onChange={(v) => onNotifChange('newRound', v)}
        />
        <Divider />
        <ToggleRow
          icon={<TrendingUp width={16} height={16} strokeWidth={2} className="text-white" />}
          iconBg="#3B7DE0"
          label="Results & scores"
          checked={notifs.results}
          onChange={(v) => onNotifChange('results', v)}
        />
        <Divider />
        <ToggleRow
          icon={<Target width={16} height={16} strokeWidth={2} className="text-white" />}
          iconBg="#E2541F"
          label="Live card updates"
          sub="Kickoff, goals & full-time on fixtures where you built a card"
          checked={notifs.liveScores}
          onChange={(v) => onNotifChange('liveScores', v)}
        />
        <Divider />
        <ToggleRow
          icon={<Swords width={16} height={16} strokeWidth={2} className="text-white" />}
          iconBg="#5B6EE1"
          label="Head-to-Head challenges"
          checked={notifs.h2hChallenges}
          onChange={(v) => onNotifChange('h2hChallenges', v)}
        />
        <Divider />
        <ToggleRow
          icon={<Users width={17} height={17} strokeWidth={2} className="text-white" />}
          iconBg="#3B7DE0"
          label="Friend activity"
          checked={notifs.friendActivity}
          onChange={(v) => onNotifChange('friendActivity', v)}
        />
        <Divider />
        <ToggleRow
          icon={<Trophy width={16} height={16} strokeWidth={2} className="text-white" />}
          iconBg="#E3A82B"
          label="Mini league activity"
          sub="Joins and round standings in your private leagues"
          checked={notifs.miniLeagues}
          onChange={(v) => onNotifChange('miniLeagues', v)}
        />
        <Divider />
        <ToggleRow
          icon={<Megaphone width={16} height={16} strokeWidth={2} className="text-white" />}
          iconBg="#7E5BD6"
          label="News & offers"
          sub="New sports, leagues and features from HeadToHead"
          checked={notifs.announcements}
          onChange={(v) => onNotifChange('announcements', v)}
        />
      </Group>
    </>
  );
}
